import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Copy, ExternalLink, Code, Settings } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

const EmbedDocs = () => {
  const { toast } = useToast();
  const [copied, setCopied] = useState<string | null>(null);

  const baseUrl = window.location.origin;
  const embedUrl = `${baseUrl}/#/embed`;

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    toast({
      title: "Copied!",
      description: `${type} code copied to clipboard`,
    });
    setTimeout(() => setCopied(null), 2000);
  };

  const embedCodes = [
    {
      title: "Standard Embed",
      description: "Full-featured certification pathway with all information",
      code: `<iframe 
  src="${embedUrl}" 
  width="100%" 
  height="800" 
  frameborder="0" 
  scrolling="auto"
  title="CSI Certification Pathway">
</iframe>`,
      preview: `${embedUrl}`
    },
    {
      title: "Compact Embed",
      description: "Condensed version for smaller spaces",
      code: `<iframe 
  src="${embedUrl}?compact=true" 
  width="100%" 
  height="600" 
  frameborder="0" 
  scrolling="auto"
  title="CSI Certification Pathway - Compact">
</iframe>`,
      preview: `${embedUrl}?compact=true`
    },
    {
      title: "Header Only",
      description: "Just the pathway without footer information",
      code: `<iframe 
  src="${embedUrl}?footer=false" 
  width="100%" 
  height="700" 
  frameborder="0" 
  scrolling="auto"
  title="CSI Certification Pathway - No Footer">
</iframe>`,
      preview: `${embedUrl}?footer=false`
    },
    {
      title: "Minimal Embed",
      description: "Pathway only, no header or footer",
      code: `<iframe 
  src="${embedUrl}?header=false&footer=false" 
  width="100%" 
  height="500" 
  frameborder="0" 
  scrolling="auto"
  title="CSI Certification Pathway - Minimal">
</iframe>`,
      preview: `${embedUrl}?header=false&footer=false`
    },
    {
      title: "Compact Minimal",
      description: "Most condensed version for tight spaces",
      code: `<iframe 
  src="${embedUrl}?compact=true&header=false&footer=false" 
  width="100%" 
  height="400" 
  frameborder="0" 
  scrolling="auto"
  title="CSI Certification Pathway - Compact Minimal">
</iframe>`,
      preview: `${embedUrl}?compact=true&header=false&footer=false`
    }
  ];

  const parameters = [
    {
      param: "compact",
      type: "boolean",
      default: "false",
      description: "Reduces spacing and font sizes for smaller displays"
    },
    {
      param: "header",
      type: "boolean", 
      default: "true",
      description: "Shows/hides the main title and description header"
    },
    {
      param: "footer",
      type: "boolean",
      default: "true", 
      description: "Shows/hides the key information footer section"
    },
    {
      param: "width",
      type: "string",
      default: "4xl",
      description: "Container max-width (sm, md, lg, xl, 2xl, 3xl, 4xl, 5xl, 6xl, 7xl)"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-blue-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-4">
            <Code className="w-8 h-8" />
            <h1 className="text-4xl md:text-5xl font-bold">
              Embed Code Generator
            </h1>
          </div>
          <p className="text-xl text-blue-100 max-w-3xl">
            Integrate the CSI Certification Pathway visual into your website with customizable embed options
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Quick Start */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Quick Start
            </CardTitle>
            <CardDescription>
              Copy and paste any of these embed codes into your HTML
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6">
              {embedCodes.map((embed, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-lg">{embed.title}</h3>
                      <p className="text-sm text-slate-600">{embed.description}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(embed.preview, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Preview
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(embed.code, embed.title)}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        {copied === embed.title ? 'Copied!' : 'Copy'}
                      </Button>
                    </div>
                  </div>
                  <pre className="bg-slate-100 p-3 rounded text-sm overflow-x-auto">
                    <code>{embed.code}</code>
                  </pre>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Parameters Reference */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>URL Parameters Reference</CardTitle>
            <CardDescription>
              Customize the embed behavior using these URL parameters
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-semibold">Parameter</th>
                    <th className="text-left p-3 font-semibold">Type</th>
                    <th className="text-left p-3 font-semibold">Default</th>
                    <th className="text-left p-3 font-semibold">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {parameters.map((param, index) => (
                    <tr key={index} className="border-b">
                      <td className="p-3">
                        <Badge variant="outline">{param.param}</Badge>
                      </td>
                      <td className="p-3 text-sm text-slate-600">{param.type}</td>
                      <td className="p-3 text-sm font-mono">{param.default}</td>
                      <td className="p-3 text-sm">{param.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Usage Examples */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Usage Examples</CardTitle>
            <CardDescription>
              Common implementation scenarios and recommended settings
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold">Website Sidebar</h3>
                <p className="text-sm text-slate-600">
                  For narrow sidebar placements, use compact mode without header
                </p>
                <pre className="bg-slate-100 p-3 rounded text-xs overflow-x-auto">
                  <code>{`?compact=true&header=false&width=sm`}</code>
                </pre>
              </div>
              
              <div className="space-y-4">
                <h3 className="font-semibold">Blog Post</h3>
                <p className="text-sm text-slate-600">
                  Full-width embed in blog content with all information
                </p>
                <pre className="bg-slate-100 p-3 rounded text-xs overflow-x-auto">
                  <code>{`?width=3xl`}</code>
                </pre>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold">Landing Page</h3>
                <p className="text-sm text-slate-600">
                  Prominent display with custom branding space
                </p>
                <pre className="bg-slate-100 p-3 rounded text-xs overflow-x-auto">
                  <code>{`?header=false&width=5xl`}</code>
                </pre>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold">Mobile App</h3>
                <p className="text-sm text-slate-600">
                  Compact version optimized for mobile screens
                </p>
                <pre className="bg-slate-100 p-3 rounded text-xs overflow-x-auto">
                  <code>{`?compact=true&width=full`}</code>
                </pre>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Technical Notes */}
        <Card>
          <CardHeader>
            <CardTitle>Technical Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-sm">
              <div>
                <h4 className="font-semibold mb-2">Responsive Design</h4>
                <p className="text-slate-600">
                  The embed automatically adapts to different screen sizes. Set iframe width to 100% for best results.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Height Recommendations</h4>
                <ul className="text-slate-600 space-y-1 ml-4">
                  <li>• Standard: 800px</li>
                  <li>• Compact: 600px</li>
                  <li>• No Footer: 700px</li>
                  <li>• Minimal: 500px</li>
                  <li>• Compact Minimal: 400px</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Security</h4>
                <p className="text-slate-600">
                  The embed uses HTTPS and includes proper CSP headers. No external dependencies beyond the host domain.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmbedDocs;